// Gateway client: cookie-authenticated WebSocket with exponential backoff and session resume.
export type GatewayStatus = "connecting" | "online" | "reconnecting" | "offline";

export class GatewayClient {
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private seq = 0;
  private attempt = 0;
  private stopped = false;
  private retryTimer: number | null = null;
  private hbTimer: number | null = null;
  private onDispatch: (t: string, d: any) => void;
  private onStatus: (s: GatewayStatus) => void;
  private onFatal: () => void;
  /** Non-dispatch server frames (e.g. `error` for a refused voice op). */
  onFrame: (op: string, d: any) => void = () => {};
  private presence: string = "online";

  constructor(handlers: { dispatch: (t: string, d: any) => void; status: (s: GatewayStatus) => void; fatal: () => void }) {
    this.onDispatch = handlers.dispatch; this.onStatus = handlers.status; this.onFatal = handlers.fatal;
    window.addEventListener("online", () => this.kick());
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") this.kick(); });
  }

  start() { this.stopped = false; this.connect(); }
  stop() {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.hbTimer) clearInterval(this.hbTimer);
    this.ws?.close(1000);
    this.ws = null; this.sessionId = null; this.seq = 0;
  }
  setPresence(status: string) {
    this.presence = status;
    this.send({ op: "presence", d: { status } });
  }

  private kick() {
    if (this.stopped || (this.ws && this.ws.readyState <= 1)) return;
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    this.connect();
  }

  send(f: unknown) { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(f)); }

  private connect() {
    this.onStatus(this.sessionId ? "reconnecting" : "connecting");
    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/gateway`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      if (this.sessionId) this.send({ op: "resume", d: { sessionId: this.sessionId, seq: this.seq } });
    };
    ws.onmessage = (ev) => {
      let f: any; try { f = JSON.parse(ev.data); } catch { return; }
      if (f.op === "hello") {
        if (this.hbTimer) clearInterval(this.hbTimer);
        this.hbTimer = window.setInterval(() => this.send({ op: "heartbeat" }), f.d.heartbeatMs * 0.8);
        return;
      }
      if (f.op === "invalid_session") { this.sessionId = null; this.seq = 0; return; }
      if (f.op === "error") { this.onFrame("error", f.d); return; }
      if (f.op !== "dispatch") return;
      if (typeof f.s === "number") this.seq = f.s;
      if (f.t === "READY") {
        this.sessionId = f.d.sessionId; this.attempt = 0; this.onStatus("online");
        if (this.presence !== "online") this.send({ op: "presence", d: { status: this.presence } });
      }
      if (f.t === "RESUMED") { this.attempt = 0; this.onStatus("online"); }
      this.onDispatch(f.t, f.d);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      if (this.hbTimer) clearInterval(this.hbTimer);
      this.ws = null;
      if (this.stopped) return;
      if (ev.code === 4004) { this.stopped = true; this.onStatus("offline"); this.onFatal(); return; } // session revoked
      this.onStatus("reconnecting");
      const delay = Math.min(30_000, 500 * 2 ** this.attempt) * (0.7 + Math.random() * 0.6);
      this.attempt++;
      this.retryTimer = window.setTimeout(() => this.connect(), delay);
    };
  }
}
